<template>
    <Teleport to="body">
        <!-- Backdrop: show instantly so blur is immediate (no transition delay) -->
        <button
            v-if="isOpen"
            aria-label="Close leaderboard"
            class="fixed inset-0 z-50 bg-black/20 backdrop-blur-md"
            @click="$emit('close')"
        />

        <!-- Panel only: transition so content animates in -->
        <Transition name="modal">
            <div v-if="isOpen"
                 class="fixed inset-0 z-50 pointer-events-none"
                 :style="modalStyle">
                <div
                    class="absolute inset-0 flex items-end justify-center lg:items-center p-0 lg:p-6 pointer-events-auto"
                    @click="$emit('close')"
                >
                    <div ref="modalRef"
                         class="relative w-full max-w-md lg:max-w-sm rounded-t-3xl lg:rounded-3xl overflow-hidden bg-zinc-950/85 backdrop-blur-2xl border border-white/10 shadow-2xl h-[72vh] flex flex-col"
                         @click.stop
                    >
                        <!-- Pull handle -->
                        <div class="lg:hidden flex justify-center pt-2.5 pb-2 cursor-grab active:cursor-grabbing"
                             @touchstart="handleTouchStart"
                             @touchmove="handleTouchMove"
                             @touchend="handleTouchEnd">
                            <div class="h-1 w-10 rounded-full bg-white/15" />
                        </div>

                        <div class="px-5 pt-3 pb-3">
                            <div class="flex items-center justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="text-white text-[15px] font-semibold tracking-tight">
                                        Top Sponsors
                                    </p>
                                </div>

                                <button
                                    @click="$emit('close')"
                                    class="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition"
                                    aria-label="Close"
                                >
                                    <X class="w-4 h-4" />
                                </button>
                            </div>

                            <template v-if="isUserLoggedIn ">
                                <button
                                    v-if="userStats.total_stars > 0"
                                    @click="handleViewMySponsorship"
                                    class="mt-3 w-full text-left rounded-2xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.07] px-4 py-3 transition"
                                >
                                    <div class="flex items-center justify-between gap-3">
                                        <div class="min-w-0">
                                            <p class="text-white/90 text-sm font-semibold">
                                                You •
                                                <span class="text-white">
                                                #{{ userStats.rank }}
                                            </span>
                                            </p>
                                            <p class="text-white/45 text-xs mt-0.5">
                                                {{ userStats.total_stars }} stars given
                                            </p>
                                        </div>
                                        <p class="text-white/40 text-xs font-medium shrink-0">
                                            View My Sponsorships →
                                        </p>
                                    </div>
                                </button>
                                <button
                                    v-else
                                    class="leaderboard-user-banner leaderboard-user-banner--cta"
                                    @click="$emit('close')"
                                >
                                    <div class="leaderboard-user-banner__inner">
                                        <div class="leaderboard-user-banner__content">
                                            <p class="leaderboard-user-banner__title leaderboard-user-banner__title--cta">
                                                Start Sponsoring to Get Ranked
                                            </p>
                                            <p class="leaderboard-user-banner__subtitle leaderboard-user-banner__subtitle--cta">
                                                Give stars to auditions and join the leaderboard
                                            </p>
                                        </div>
                                        <p class="leaderboard-user-banner__action leaderboard-user-banner__action--cta">
                                            Browse →
                                        </p>
                                    </div>
                                </button>
                            </template>
                        </div>

                        <div class="h-px bg-white/10" />

                        <div ref="scrollContainer"
                             @scroll="handleScroll"
                             class="scroll-container flex-1 min-h-0 overflow-y-auto overscroll-contain"
                        >
                            <!-- Initial loading skeletons -->
                            <div v-if="isInitialLoading" class="px-2 py-2 space-y-0 animate-pulse">
                                <div v-for="i in 6" :key="`init-skeleton-${i}`">
                                    <div class="rounded-2xl px-4 py-3 flex items-center gap-3">
                                        <div class="w-6 h-4 bg-white/10 rounded shrink-0"></div>
                                        <div class="w-8 h-8 rounded-full bg-white/10 shrink-0"></div>
                                        <div class="flex-1 space-y-1.5">
                                            <div class="h-3.5 bg-white/10 rounded w-28"></div>
                                            <div class="h-3 bg-white/[0.06] rounded w-20"></div>
                                        </div>
                                    </div>
                                    <div class="mx-6 h-px bg-white/8" />
                                </div>
                            </div>

                            <div v-else class="px-2 py-2">
                                <div v-for="entry in allSponsors" :key="entry.user_id">
                                    <div @click="goToMyProfile(entry)" class="flex items-center gap-3 px-4 py-3 rounded-2xl transition hover:bg-white/[0.04] cursor-pointer">
                                        <!-- Rank number -->
                                        <div class="w-6 shrink-0 text-right">
                                            <span
                                                :class="[
                                                    'text-xs font-semibold',
                                                    entry.rank === 1
                                                        ? 'text-white'
                                                        : isTop(entry.rank)
                                                        ? 'text-white/65'
                                                        : 'text-white/35'
                                                ]"
                                            >
                                                {{ entry.rank }}
                                            </span>
                                        </div>

                                        <!-- Avatar -->
                                        <div
                                            :class="[
                                                'w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0',
                                                getAvatarBg(entry.rank)
                                            ]"
                                        >
                                            {{ getInitial(entry.sponsor_name) }}
                                        </div>

                                        <!-- Username and stats -->
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center gap-2 min-w-0">
                                                <p class="text-white/90 text-sm font-medium truncate">
                                                    {{ entry.sponsor_name }}
                                                </p>

                                                <span
                                                    v-if="entry.rank === 1"
                                                    class="inline-flex items-center px-2 py-0.5 rounded-full border border-white/15 bg-white/[0.06] text-xs font-semibold text-white/80"
                                                >
                                                    Leader
                                                </span>
                                            </div>

                                            <p class="text-white/45 text-xs mt-0.5">
                                                <span class="text-white/75 font-semibold">
                                                    {{ entry.total_stars.toLocaleString() }}
                                                </span>
                                                stars • {{ entry.auditions_sponsored }} auditions
                                            </p>
                                        </div>

                                        <!-- Rank indicator dot -->
                                        <div class="shrink-0 flex items-center justify-center w-4">
                                            <div
                                                :class="[
                                                    'w-2.5 h-2.5 rounded-full',
                                                    getDotClass(entry.rank),
                                                    getDotRing(entry.rank)
                                                ]"
                                            />
                                        </div>
                                    </div>

                                    <div class="mx-6 h-px bg-white/8" />
                                </div>

                                <!-- Loading with Skeleton -->
                                <div v-if="isLoading" class="space-y-0">
                                    <div v-for="i in 3" :key="`skeleton-${i}`" class="animate-pulse">
                                        <div class="rounded-2xl px-4 py-3 flex items-center gap-3">
                                            <!-- Rank skeleton -->
                                            <div class="w-6 h-4 bg-white/10 rounded shrink-0"></div>

                                            <!-- Avatar skeleton -->
                                            <div class="w-8 h-8 rounded-full bg-white/10 shrink-0"></div>

                                            <!-- Name skeleton -->
                                            <div class="flex-1">
                                                <div class="h-4 bg-white/10 rounded w-24"></div>
                                            </div>
                                        </div>
                                        <div class="mx-6 h-px bg-white/8" />
                                    </div>
                                </div>

                                <!-- End of list -->
                                <div v-if="!hasMorePages && allSponsors.length > 0" class="text-center py-4">
                                    <p class="text-white/40 text-xs">
                                        That's everyone!
                                    </p>
                                </div>
                            </div><!-- end v-else -->
                        </div>

                        <div class="px-5 py-4 border-t border-white/10 bg-black/10">
                            <template v-if="!isUserLoggedIn">
                                <button
                                    @click="handleJoinClick"
                                    class="w-full h-11 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition"
                                >
                                    Get Stars to Compete
                                </button>
                                <p class="mt-2 text-center text-xs text-white/35">
                                    Get stars monthly + access to 14k+ videos
                                </p>
                            </template>

                            <template v-else>
                                <button
                                    @click="$emit('close')"
                                    class="w-full h-11 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold transition"
                                >
                                    Browse Auditions
                                </button>
                                <p class="mt-2 text-center text-xs text-white/35">
                                    Sponsor auditions to climb the leaderboard
                                </p>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {computed, nextTick, onUnmounted, ref, watch} from 'vue'
import { X } from 'lucide-vue-next'
import {router} from "@inertiajs/vue3";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    isUserLoggedIn: {
        type: Boolean,
    },
    isInitialLoading: {
        type: Boolean,
        default: false
    },
    leaderboard: {
        type: Array,
        required: true
    },
    userStats: {
        type: Object,
        required: true
    },
    routePrefix: {
        type: String,
        required: true
    }
})

const emit = defineEmits(['close', 'viewMine', 'joinClick'])

// Touch/swipe handling
const modalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value || !modalRef.value) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {} // Don't allow swipe up

    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none'
    }
})

const handleTouchStart = (e) => {
    touchStartY.value = e.touches[0].clientY
    touchCurrentY.value = e.touches[0].clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return

    const touch = e.touches[0]
    touchCurrentY.value = touch.clientY

    const deltaY = touchCurrentY.value - touchStartY.value

    // Prevent page scrolling when dragging down
    if (deltaY > 0) {
        e.preventDefault()
    }
}

const handleTouchEnd = () => {
    if (!isDragging.value) return

    const deltaY = touchCurrentY.value - touchStartY.value
    const threshold = 100 // Close if dragged more than 100px

    if (deltaY > threshold) {
        emit('close')
    }

    // Reset state
    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}

// Infinite scroll state
const allSponsors = ref([])
const currentPage = ref(1)
const isLoading = ref(false)
const hasMorePages = ref(true)
const scrollContainer = ref(null)

// After data loads, check if the container can actually scroll; if not, load more
const fillContainerIfNeeded = async () => {
    await nextTick()
    if (!scrollContainer.value || !hasMorePages.value || isLoading.value) return
    if (scrollContainer.value.scrollHeight <= scrollContainer.value.clientHeight) {
        loadMoreSponsors()
    }
}

// Initialize / update with first page data when it arrives
watch(() => props.leaderboard, (newSponsors) => {
    if (newSponsors && newSponsors.data) {
        allSponsors.value = [...newSponsors.data]
        currentPage.value = newSponsors.current_page
        hasMorePages.value = newSponsors.current_page < newSponsors.last_page
        fillContainerIfNeeded()
    }
}, { immediate: true })

// Lock/unlock body scroll when modal opens or closes
const lockBodyScroll = () => {
    document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
    document.body.style.overflow = ''
}

onUnmounted(() => {
    unlockBodyScroll()
})

// Reset when modal opens
watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
        lockBodyScroll()

        // Reset to initial state (guard against leaderboard not yet loaded)
        if (!props.leaderboard?.data) return
        allSponsors.value = [...props.leaderboard.data]
        currentPage.value = props.leaderboard.current_page
        hasMorePages.value = props.leaderboard.current_page < props.leaderboard.last_page

        // Reset scroll position
        if (scrollContainer.value) {
            scrollContainer.value.scrollTop = 0
        }

        // Reset drag state
        isDragging.value = false
        touchStartY.value = 0
        touchCurrentY.value = 0
    } else {
        unlockBodyScroll()
    }
})

// Load more sponsors
const loadMoreSponsors = async () => {
    if (isLoading.value || !hasMorePages.value) return

    isLoading.value = true

    try {
        const nextPage = currentPage.value + 1
        const response = await axios.get(route(props.routePrefix + '.reels.global-top-sponsors', {
            page: nextPage,
            per_page: 20
        }))

        const newData = response.data

        // Append new sponsors to the list
        allSponsors.value = [...allSponsors.value, ...newData.data]
        currentPage.value = newData.current_page
        hasMorePages.value = newData.current_page < newData.last_page

    } catch (error) {
        console.error('Failed to load more sponsors:', error)
    } finally {
        isLoading.value = false
        // If still not scrollable after loading, keep filling
        fillContainerIfNeeded()
    }
}

// Detect when user scrolls near bottom
const handleScroll = (e) => {
    const container = e.target
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight

    // Load more when 200px from bottom
    const threshold = 200
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight

    if (distanceFromBottom < threshold && hasMorePages.value && !isLoading.value) {
        loadMoreSponsors()
    }
}

const isTop = (rank) => rank <= 3

const getInitial = (name) => name.charAt(0).toUpperCase()

const getAvatarBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-br from-amber-400 to-amber-600'
    if (rank === 2) return 'bg-gradient-to-br from-zinc-300 to-zinc-500'
    if (rank === 3) return 'bg-gradient-to-br from-amber-600 to-amber-800'
    return 'bg-blue-600'
}

const getDotClass = (rank) => {
    if (rank === 1) return 'bg-amber-300'
    if (rank === 2) return 'bg-zinc-200/80'
    if (rank === 3) return 'bg-amber-700/70'
    return 'bg-transparent'
}

const getDotRing = (rank) => {
    if (rank === 1) return 'shadow-[0_0_0_2px_rgba(251,191,36,0.18)]'
    if (rank === 2) return 'shadow-[0_0_0_2px_rgba(228,228,231,0.14)]'
    if (rank === 3) return 'shadow-[0_0_0_2px_rgba(180,83,9,0.14)]'
    return ''
}

const handleViewMySponsorship = () => {
    emit('close')
    emit('viewMine')
}

const handleJoinClick = () => {
    emit('close')
    emit('joinClick')
}

const goToMyProfile = (entry) => {
    const url = props.isUserLoggedIn ?
        route('members2.profile.my', { slug: entry.sponsor_name }) :
        route(props.routePrefix + '.profile.my', { slug: entry.sponsor_name })

    router.visit(url, {
        preserveScroll: false,
        preserveState: true,
    })
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

/* Add smooth transition when releasing drag */
.relative {
    transition: transform 0.3s ease-out;
}

/* Consistent scrollbar across desktop and mobile */
.scroll-container {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
}

.scroll-container::-webkit-scrollbar {
    width: 4px;
}

.scroll-container::-webkit-scrollbar-track {
    background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 9999px;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.3);
}

.leaderboard-user-banner {
    margin-top: 0.75rem;
    width: 100%;
    text-align: left;
    border-radius: 1rem;
    padding: 0.75rem 1rem;
    transition: background-color 150ms;
    cursor: pointer;
}

.leaderboard-user-banner--cta {
    border: 1px solid rgba(59, 130, 246, 0.3);
    background-color: rgba(59, 130, 246, 0.1);
}

.leaderboard-user-banner--cta:hover {
    background-color: rgba(59, 130, 246, 0.15);
}

.leaderboard-user-banner__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.leaderboard-user-banner__content {
    min-width: 0;
    flex: 1;
}

.leaderboard-user-banner__title {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.25;
}

.leaderboard-user-banner__title--cta {
    color: rgb(255, 255, 255);
}

.leaderboard-user-banner__subtitle {
    font-size: 0.75rem;
    margin-top: 0.125rem;
    line-height: 1.25;
}

.leaderboard-user-banner__subtitle--cta {
    color: rgba(255, 255, 255, 0.6);
}

.leaderboard-user-banner__action {
    font-size: 0.75rem;
    font-weight: 500;
    flex-shrink: 0;
    white-space: nowrap;
}

.leaderboard-user-banner__action--cta {
    color: rgb(96, 165, 250);
    font-weight: 600;
}
</style>
